/**
 * ⭐⭐ **THE ONE-TIME SECRET REVEAL — a plaintext credential with no second chance, shown once.**
 *
 * # Why this is a LIBRARY primitive and not two page-local dialogs
 *
 * It had shipped twice, independently, and the two copies had already drifted: one Close was a
 * `<Button>` and the other a hand-rolled `<button className=…>`; one panel used
 * `dataToneClass('caution','panel')` and the other a hand-picked `border-state-warning/60`; the code
 * blocks were at two different type rungs. ⛔ **Neither drift is visible from inside either file** —
 * each is internally consistent — and both call sites are six inches from each other in the same
 * console. Two more adopters were about to land (`createScimClient` and `rotateScimToken`, whose
 * tokens are likewise never re-fetchable), which would have made it four.
 *
 * ## ⛔ AND THE ALTERNATIVE WAS NOT "LEAVE IT" — IT WAS A FOURTH RATCHET EXEMPTION
 *
 * `primitive-adoption.test.ts` pins the hand-rolled `<input type="checkbox">` population of
 * `src/panes` as an **exact named set**, and its failure message says in terms: *"if it is genuinely
 * not a filter, add it here WITH the reason — do not raise a number."* A SCIM ack checkbox authored in
 * `src/panes` is a fourth entry and reds **by name**. ⇒ Adding exemptions for the same shape is
 * precisely the fork the rule exists to stop; **lifting to `src/shell/` removes it from the corpus**,
 * which is the only compliant route.
 *
 * # The five conditions. Each one is load-bearing.
 *
 *  1. **Copy-acknowledge gate**: Close is held (`aria-disabled`, keeps its Tab stop) until the
 *     operator confirms they copied it. A Copy button writes the secret to the clipboard and says
 *     `Copied.` only after the write resolves; its name never carries the secret. With no Clipboard
 *     API, it says the copy failed.
 *  2. Never in the URL, `history.pushState`, `localStorage`, `sessionStorage`, a `console.*` call or
 *     any telemetry payload. This component writes to none of them. The secret lives in the caller's
 *     React state and in this DOM node. A press of Copy also writes it to the system clipboard, and
 *     other apps or a clipboard history can read it there after the dialog closes.
 *  3. ⛔ The caller clears it from state on close ⇒ **re-opening cannot re-display it**, because the
 *     server will never send it again.
 *  4. The copy says **shown once**, and ⛔ never claims the secret was delivered anywhere.
 *  5. ⛔ **No fragment afterwards** — no last-four, no host-only preview. A suffix on a later screen
 *     could only come from client retention, which condition 3 forbids.
 *
 * # ⛔⛔ `onEscape={null}` AND THE HELD CLOSE ARE FIXED HERE, NOT PROPS
 *
 * A one-time secret that a stray Escape can dismiss is a secret the operator never read — Escape would
 * route around the very gate this component is. Making either configurable is how the next call site
 * gets it wrong, and *"the next call site"* is the whole reason this file exists. ⚠️ `dialog-panel.tsx`
 * already documents `onEscape`'s nullability as existing **for this exact panel**; the nullable prop
 * and this component are two halves of one decision.
 */
import { useId, useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from './Button';
import { dataToneClass } from './data-tone';
import { DialogPanel } from '../panes/dialog-panel';

export interface OneTimeSecretDialogProps {
  /**
   * The heading, as a node the caller renders — ⛔ **it must carry `id={`${testId}-title`}`**, which
   * is what {@link DialogPanel}'s `aria-labelledby` resolves against.
   *
   * ⚠️ A node rather than a string because both call sites interpolate a subject into it (*"Accept
   * link for {email}"*), and a `title: string` + `subject: string` pair would be two props modelling
   * one sentence.
   */
  readonly titleNode: React.ReactNode;
  /**
   * The explanation, as a node the caller renders.
   *
   * ⛔ **The caller's, deliberately** — and not only because the two sentences differ. *"The control
   * plane stores no token material"* and *"when its mail service is on, the control plane also tries to
   * mail it"* are claims about two different producers, and a library component cannot make either
   * of them truthfully. ⚠️ It also keeps each pane's prose measure in that pane's own file,
   * where `prose-measure-preservation.test.ts` anchors it.
   */
  readonly bodyCopy: React.ReactNode;
  /**
   * The plaintext secret. ⛔ Rendered exactly once, in a `<code>` — see {@link OneTimeSecretDialog}.
   *
   * A list renders each member on its own line inside the same `<code>`, in the given order: the
   * ten recovery codes of a CP operator (kernel apps stage 5, WS-31b, R-152 Q-5) are one secret
   * shown once, and a reader copies and counts them line by line.
   */
  readonly secret: string | readonly string[];
  /** The checkbox's label — *"I have copied this token."* / *"…this link."* */
  readonly ackLabel: string;
  /**
   * The accessible name of the Copy button: what the caller's secret is, such as "Copy the invite
   * link" or "Copy the recovery codes". Default "Copy the token". It never carries the secret.
   */
  readonly copyLabel?: string;
  readonly onClose: () => void;
  readonly testId: string;
  /**
   * Prefix for the parts (`-value`, `-line`, `-copy-button`, `-copy-status`, `-ack`, `-close`).
   * Defaults to `testId`.
   *
   * ## ⛔ WHY THIS EXISTS — the same reason and scar as the deleted `FilterGroupProps.optionTestId`
   *
   * The two lifted call sites do not share one prefix: `PeoplePane` is `people-invite-link` /
   * `people-invite-link-ack`, but `DataPlaneActions` is **`dp-token-modal`** on the panel and
   * **`dp-token-…`** on the parts. Deriving both from one `testId` would rename `dp-token-ack`,
   * `dp-token-value` and `dp-token-close` — twelve live assertions in that pane's own suite, plus the
   * `primitive-adoption` needle that is delimiter-bounded *precisely* to catch that rename.
   *
   * ⭐ **A primitive that forces its adopters to rename their test ids does not get adopted**, and a
   * lift that changes observed behaviour is a rewrite. One optional prop with a sane default is the
   * cheaper half of that trade — `FilterGroup` reached the same conclusion from the same evidence.
   */
  readonly partTestId?: string;
}

/** The text of the Copy status line per outcome. Empty while no copy ran. */
const COPY_STATUS: Readonly<Record<'idle' | 'copied' | 'failed', string>> = {
  idle: '',
  copied: 'Copied.',
  failed: 'The browser did not copy it. Select the text and copy it by hand.',
};

/**
 * The reveal panel.
 *
 * ⛔ The secret is a `<code>` block. **Never an `<a href>`** — a browser offers to open or copy a link
 * target and a visited-link entry is retention — and **never an `<input name>` inside a form**, which a
 * password manager could offer to save. Both were reasoned out at the invitation call site and both
 * apply to every secret this will ever show.
 */
export function OneTimeSecretDialog({
  titleNode,
  bodyCopy,
  secret,
  ackLabel,
  copyLabel = 'Copy the token',
  onClose,
  testId,
  partTestId,
}: OneTimeSecretDialogProps): React.JSX.Element {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copy, setCopy] = useState<keyof typeof COPY_STATUS>('idle');
  const ackId = useId();
  /* The clipboard gets the text the code block shows: a list goes one member per line. */
  const copyText = typeof secret === 'string' ? secret : secret.join('\n');
  /* ⛔ Defaults to `testId`, so the common case stays one prop. See {@link OneTimeSecretDialogProps.partTestId}. */
  const partId = partTestId ?? testId;
  return (
    <DialogPanel
      labelledBy={`${testId}-title`}
      testId={testId}
      /* ⛔ FIXED, not a prop. See this file's header. */
      onEscape={null}
      /* ⛔ `dataToneClass('caution','panel')` — one value, chosen once. The two lifted sites had
         `/60` and `/50` on the same semantic frame, which is the alpha zoo `data-tone.tsx` ended. */
      className={`flex flex-col gap-3 rounded-lg border p-4 ${dataToneClass('caution', 'panel')}`}
    >
      {titleNode}
      {bodyCopy}
      <code
        data-testid={`${partId}-value`}
        className="block break-all rounded border border-border-subtle bg-surface-muted px-3 py-2 font-mono text-xs text-primary"
      >
        {typeof secret === 'string'
          ? secret
          : secret.map((line, index) => (
              <span key={index} data-testid={`${partId}-line`} className="block">
                {line}
              </span>
            ))}
      </code>
      <div className="flex items-center gap-2">
        {/* The name never carries the secret, so a screen reader does not speak it on focus. */}
        <Button
          type="button"
          aria-label={copyLabel}
          data-testid={`${partId}-copy-button`}
          onClick={() => {
            setCopy('idle');
            /* An insecure page or an old browser has no Clipboard API, so the copy fails in words. */
            const clipboard: Clipboard | undefined = navigator.clipboard;
            if (clipboard === undefined) {
              /* In a later task, so the empty line renders first and a reader hears the text again. */
              setTimeout(() => setCopy('failed'), 0);
              return;
            }
            clipboard.writeText(copyText).then(
              () => setCopy('copied'),
              () => setCopy('failed'),
            );
          }}
        >
          <Copy aria-hidden="true" className="h-3 w-3" />
          Copy
        </Button>
        {/* Always mounted and empty while idle, so a screen reader hears the outcome. */}
        <span role="status" data-testid={`${partId}-copy-status`} className="text-xs text-secondary">
          {COPY_STATUS[copy]}
        </span>
      </div>
      <label id={ackId} className="flex items-center gap-2 text-xs text-secondary">
        {/*
          ⛔ A hand-rolled `<input type="checkbox">`, not a filter control: it is an acknowledgement
          gate, not a filter, and the ratchet's own docblock names this exact control as one of the
          three deliberate non-filters. ⭐ Living in `src/shell/` is what takes it out of the ratchet's
          corpus honestly, rather than by an exemption entry that has to be argued each time.
        */}
        <input
          type="checkbox"
          data-testid={`${partId}-ack`}
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        {ackLabel}
      </label>
      <div>
        {/* ⛔ CONDITION 1, the gate. Held, Close keeps its Tab stop, names the box and ignores a press. */}
        <Button
          type="button"
          onClick={() => {
            if (acknowledged) onClose();
          }}
          aria-disabled={acknowledged ? undefined : 'true'}
          aria-describedby={acknowledged ? undefined : ackId}
          data-testid={`${partId}-close`}
        >
          Close
        </Button>
      </div>
    </DialogPanel>
  );
}
